import { jsPDF } from "jspdf";
import type { Budget, Company } from "../types";
import {
  formatBudgetCurrency,
  getBudgetLineLabel,
  getBudgetServices,
  getBudgetStatusLabel,
  normalizeBudgetSubServices,
  resolveBudgetTotal,
} from "./budgetUtils";
import { formatDateShort } from "./dateUtils";
import { displayCnpj, formatInstagramForDisplay } from "./cnpjUtils";
import { displayPhoneNumber } from "./phoneUtils";
import { buildWhatsAppUrl } from "./whatsappUtils";
import { loadPdfContactIcons, type PdfContactIcons } from "./pdfIcons";
import { getCarLogoUrl } from "./carLogoUtils";

const LOGO_SIZE_MM = 24;
const CAR_BRAND_SIZE_MM = (50 * 25.4) / 96; // 50×50px at 96dpi
const CLIENT_INFO_FONT_SIZE = 9;
const CLIENT_INFO_LINE_GAP_MM = 5;
const PAGE_WIDTH = 210;
const PAGE_HEIGHT = 297;
const MARGIN = 14;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
const ICON_SIZE_MM = 3.5;
const ICON_TEXT_GAP_MM = 1.5;
const CONTACT_ROW_HEIGHT_MM = 5;
const SIGNATURE_WIDTH_MM = 50;
const SIGNATURE_HEIGHT_MM = 20;
const FOOTER_RESERVE_MM = 38;

type LogoImage = {
  data: string;
  format: "PNG" | "JPEG";
  widthPx?: number;
  heightPx?: number;
};

type JsPdfWithLink = jsPDF & {
  textWithLink?: (
    text: string,
    x: number,
    y: number,
    options: { url: string }
  ) => void;
};

function drawLinkedText(
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  url: string
): void {
  const linked = doc as JsPdfWithLink;
  if (typeof linked.textWithLink === "function") {
    linked.textWithLink(text, x, y, { url });
    return;
  }

  doc.text(text, x, y);
  doc.link(x, y - 3.5, doc.getTextWidth(text), 4, { url });
}

function drawIconContactRow(
  doc: jsPDF,
  x: number,
  y: number,
  iconData: string,
  text: string,
  url?: string,
  maxTextWidth?: number
): number {
  const iconY = y - ICON_SIZE_MM + 0.8;

  try {
    doc.addImage(iconData, "PNG", x, iconY, ICON_SIZE_MM, ICON_SIZE_MM);
  } catch {
    // Skip icon if image data is invalid
  }

  const textX = x + ICON_SIZE_MM + ICON_TEXT_GAP_MM;
  doc.setFontSize(9);
  doc.setFont("helvetica", "normal");
  doc.setTextColor(90);

  const lines = maxTextWidth
    ? doc.splitTextToSize(text, maxTextWidth - ICON_SIZE_MM - ICON_TEXT_GAP_MM)
    : [text];

  if (url && lines.length === 1) {
    drawLinkedText(doc, lines[0], textX, y, url);
    doc.link(x, iconY, ICON_SIZE_MM + ICON_TEXT_GAP_MM + doc.getTextWidth(lines[0]), ICON_SIZE_MM, {
      url,
    });
  } else {
    doc.text(lines, textX, y);
    if (url) {
      const textWidth = Math.max(...lines.map((line: string) => doc.getTextWidth(line)));
      const rowHeight = Math.max(ICON_SIZE_MM, lines.length * 4);
      doc.link(x, iconY, ICON_SIZE_MM + ICON_TEXT_GAP_MM + textWidth, rowHeight, {
        url,
      });
    }
  }

  return y + Math.max(CONTACT_ROW_HEIGHT_MM, lines.length * 4.5);
}

function ensureFooterSpace(doc: jsPDF, y: number): number {
  const maxContentY = PAGE_HEIGHT - MARGIN - FOOTER_RESERVE_MM;
  if (y <= maxContentY) return y;

  doc.addPage();
  return MARGIN;
}

function drawPdfFooter(
  doc: jsPDF,
  signature: LogoImage | null,
  isPaidConfirmation: boolean,
  footerText?: string
): void {
  const footerY = PAGE_HEIGHT - MARGIN;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(120);
  doc.text(
    footerText ??
      (isPaidConfirmation
        ? "Este documento confirma o pagamento dos serviços listados."
        : "Orçamento válido por 7 dias a partir da última atualização."),
    MARGIN,
    footerY - 22
  );

  if (signature) {
    const sigX = PAGE_WIDTH - MARGIN - SIGNATURE_WIDTH_MM;
    const sigY = footerY - SIGNATURE_HEIGHT_MM - 10;

    try {
      doc.addImage(
        signature.data,
        signature.format,
        sigX,
        sigY,
        SIGNATURE_WIDTH_MM,
        SIGNATURE_HEIGHT_MM
      );
    } catch {
      // Continue without signature if image data is invalid
    }

    doc.setDrawColor(180);
    doc.line(sigX, footerY - 6, sigX + SIGNATURE_WIDTH_MM, footerY - 6);

    doc.setFontSize(8);
    doc.setTextColor(100);
    doc.text("Assinatura", sigX + SIGNATURE_WIDTH_MM / 2, footerY - 2, {
      align: "center",
    });
  }
}

function drawWrappedDescription(
  doc: jsPDF,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeightMm: number
): number {
  const trimmed = text.trim();
  if (!trimmed) return y;

  const lines = doc.splitTextToSize(trimmed, maxWidth);
  doc.text(lines, x, y);
  return y + lines.length * lineHeightMm;
}

async function measureImage(
  dataUrl: string
): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () =>
      resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => reject(new Error("Failed to measure image"));
    img.src = dataUrl;
  });
}

function fitImageInBoxMm(
  widthPx: number,
  heightPx: number,
  boxMm: number
): { widthMm: number; heightMm: number } {
  if (widthPx <= 0 || heightPx <= 0) {
    return { widthMm: boxMm, heightMm: boxMm };
  }

  const aspect = widthPx / heightPx;
  if (aspect >= 1) {
    return { widthMm: boxMm, heightMm: boxMm / aspect };
  }

  return { widthMm: boxMm * aspect, heightMm: boxMm };
}

async function loadLogoImage(logo?: string): Promise<LogoImage | null> {
  if (!logo?.trim()) return null;

  let data: string;
  let format: "PNG" | "JPEG";

  if (logo.startsWith("data:")) {
    data = logo;
    format =
      logo.includes("image/jpeg") || logo.includes("image/jpg") ? "JPEG" : "PNG";
  } else {
    try {
      const response = await fetch(logo);
      if (!response.ok) return null;
      const blob = await response.blob();
      data = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(new Error("Failed to read image"));
        reader.readAsDataURL(blob);
      });
      format =
        data.includes("image/jpeg") || data.includes("image/jpg")
          ? "JPEG"
          : "PNG";
    } catch {
      return null;
    }
  }

  try {
    const { width, height } = await measureImage(data);
    return { data, format, widthPx: width, heightPx: height };
  } catch {
    return { data, format };
  }
}

function drawWhatsAppPhoneLine(
  doc: jsPDF,
  x: number,
  y: number,
  phoneNumber: string,
  icons: PdfContactIcons,
  maxTextWidth?: number
): number {
  const formatted = displayPhoneNumber(phoneNumber);
  const whatsappUrl = buildWhatsAppUrl(phoneNumber);
  return drawIconContactRow(
    doc,
    x,
    y,
    icons.whatsapp,
    formatted,
    whatsappUrl,
    maxTextWidth
  );
}

export type BudgetPdfOptions = {
  showServicePrices?: boolean;
  showSubServicePrices?: boolean;
  /** When set, used as-is (no #id). */
  documentTitle?: string;
  /** Overrides default orcamento-/comprovante-pagamento- prefix (id still appended unless fileName is set). */
  filePrefix?: string;
  /** Full filename without .pdf — when set, used instead of prefix-id-client. */
  fileName?: string;
  /** Overrides deriving paid confirmation from budget.status === "Completed". */
  isPaidConfirmation?: boolean;
  /** Overrides default footer copy. */
  footerText?: string;
  /** Label for the primary date line when not a paid confirmation (default: "Data"). */
  dateLabel?: string;
  /** Label for status when not a paid confirmation (default: budget status label). */
  statusLabel?: string;
};

export async function downloadBudgetPdf(
  budget: Budget,
  company: Company | null,
  options: BudgetPdfOptions = {}
): Promise<void> {
  const showServicePrices = options.showServicePrices !== false;
  const showSubServicePrices = options.showSubServicePrices !== false;
  const showValorColumn = showServicePrices || showSubServicePrices;

  const doc = new jsPDF();
  let y = MARGIN;

  const isPaidConfirmation =
    options.isPaidConfirmation ?? budget.status === "Completed";
  const documentTitle =
    options.documentTitle ??
    (isPaidConfirmation
      ? `Comprovante de Pagamento #${budget.id}`
      : `Orçamento #${budget.id}`);
  const companyName = company?.name ?? "Empresa";
  const clientName = budget.client?.name ?? `Cliente #${budget.clientId}`;
  const createdAt = formatDateShort(budget.createdAt.split("T")[0]);
  const paidAt = formatDateShort(
    (budget.updatedAt || budget.createdAt).split("T")[0]
  );
  const services = getBudgetServices(budget);
  const total = resolveBudgetTotal(budget);
  const [logo, icons, signature, carBrandImage] = await Promise.all([
    loadLogoImage(company?.logo),
    loadPdfContactIcons(),
    loadLogoImage(company?.signature ?? undefined),
    budget.clientCar?.brand
      ? loadLogoImage(getCarLogoUrl(budget.clientCar.brand) ?? undefined)
      : Promise.resolve(null),
  ]);

  const headerTextX = logo ? MARGIN + LOGO_SIZE_MM + 6 : MARGIN;
  const headerTextWidth = CONTENT_WIDTH - (headerTextX - MARGIN);
  const headerStartY = y;

  if (logo) {
    try {
      doc.addImage(
        logo.data,
        logo.format,
        MARGIN,
        y,
        LOGO_SIZE_MM,
        LOGO_SIZE_MM
      );
    } catch {
      // Continue without logo if image data is invalid
    }
  }

  let textY = headerStartY + 5;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(30);
  doc.text(companyName, headerTextX, textY);
  textY += 6;

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(90);

  if (company?.address?.trim()) {
    const addressLines = doc.splitTextToSize(
      company.address.trim(),
      headerTextWidth
    );
    doc.text(addressLines, headerTextX, textY);
    textY += addressLines.length * 4.5;
  }

  if (company?.cnpj?.trim()) {
    doc.text(`CNPJ: ${displayCnpj(company.cnpj)}`, headerTextX, textY);
    textY += 5;
  }

  if (company?.phoneNumber?.trim()) {
    textY = drawWhatsAppPhoneLine(
      doc,
      headerTextX,
      textY,
      company.phoneNumber,
      icons,
      headerTextWidth
    );
  }

  if (company?.instagramUrl?.trim()) {
    const instagram = formatInstagramForDisplay(company.instagramUrl);
    textY = drawIconContactRow(
      doc,
      headerTextX,
      textY,
      icons.instagram,
      instagram,
      company.instagramUrl.trim(),
      headerTextWidth
    );
  }

  y = Math.max(headerStartY + LOGO_SIZE_MM, textY) + 8;

  doc.setDrawColor(220);
  doc.line(MARGIN, y, PAGE_WIDTH - MARGIN, y);
  y += 8;

  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.setTextColor(0);
  doc.text(documentTitle, MARGIN, y);
  y += 8;

  if (isPaidConfirmation) {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(34, 139, 84);
    doc.text("PAGO", MARGIN, y);
    doc.setTextColor(0);
    y += 7;
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(CLIENT_INFO_FONT_SIZE);
  doc.setTextColor(60);

  const clientInfoStartY = y;
  const vehicleTextMaxWidth =
    CONTENT_WIDTH - (carBrandImage ? CAR_BRAND_SIZE_MM + 6 : 0);

  doc.text(`Cliente: ${clientName}`, MARGIN, y);
  y += CLIENT_INFO_LINE_GAP_MM;

  let clientInfoEndY = y;

  if (budget.clientCar) {
    const vehicleLabel = `Veículo: ${budget.clientCar.model} — ${budget.clientCar.plate}`;
    const lines = doc.splitTextToSize(vehicleLabel, vehicleTextMaxWidth);
    doc.text(lines, MARGIN, y);
    clientInfoEndY = y + Math.max(0, lines.length - 1) * 4;
    y = clientInfoEndY + CLIENT_INFO_LINE_GAP_MM;
  }

  if (carBrandImage && budget.clientCar) {
    try {
      const { widthMm, heightMm } = fitImageInBoxMm(
        carBrandImage.widthPx ?? 1,
        carBrandImage.heightPx ?? 1,
        CAR_BRAND_SIZE_MM
      );
      const logoX = PAGE_WIDTH - MARGIN - widthMm;
      const blockHeight = clientInfoEndY - clientInfoStartY + 3;
      const logoY =
        clientInfoStartY - 2 + Math.max(0, (blockHeight - heightMm) / 2);

      doc.addImage(
        carBrandImage.data,
        carBrandImage.format,
        logoX,
        logoY,
        widthMm,
        heightMm
      );
    } catch {
      // Continue without brand logo if image data is invalid
    }
  }

  if (isPaidConfirmation) {
    doc.text(`Data do orçamento: ${createdAt}`, MARGIN, y);
    y += CLIENT_INFO_LINE_GAP_MM;
    doc.text(`Data do pagamento: ${paidAt}`, MARGIN, y);
    y += CLIENT_INFO_LINE_GAP_MM;
    doc.text("Status: Pago", MARGIN, y);
  } else {
    const dateLabel = options.dateLabel ?? "Data";
    doc.text(`${dateLabel}: ${createdAt}`, MARGIN, y);
    y += CLIENT_INFO_LINE_GAP_MM;
    doc.text(
      `Status: ${options.statusLabel ?? getBudgetStatusLabel(budget.status)}`,
      MARGIN,
      y
    );
  }
  doc.setTextColor(0);
  y += 10;

  doc.setFontSize(10);
  doc.setFont("helvetica", "bold");
  doc.text("Serviço", MARGIN, y);
  if (showValorColumn) {
    doc.text("Valor", PAGE_WIDTH - MARGIN, y, { align: "right" });
  }
  doc.setFont("helvetica", "normal");
  y += 6;

  doc.line(MARGIN, y, PAGE_WIDTH - MARGIN, y);
  y += 6;

  for (const line of services) {
    const name = getBudgetLineLabel(line);
    const serviceDescription = line.service?.description?.trim() ?? "";
    const price = formatBudgetCurrency(line.price);
    const subs = normalizeBudgetSubServices(line);
    const nameMaxWidth = showValorColumn ? 120 : CONTENT_WIDTH;
    const nameLines = doc.splitTextToSize(name, nameMaxWidth);
    const descriptionPreviewLines = serviceDescription
      ? doc.splitTextToSize(serviceDescription, nameMaxWidth - 2)
      : [];

    const estimatedHeight =
      nameLines.length * 5 +
      descriptionPreviewLines.length * 3.8 +
      subs.length * 8;

    if (y + estimatedHeight > PAGE_HEIGHT - MARGIN - FOOTER_RESERVE_MM) {
      doc.addPage();
      y = MARGIN;
    }

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(0);
    doc.text(nameLines, MARGIN, y);
    if (showServicePrices) {
      doc.text(price, PAGE_WIDTH - MARGIN, y, { align: "right" });
    }
    y += Math.max(nameLines.length * 5, 6);

    if (serviceDescription) {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor(110);
      y = drawWrappedDescription(
        doc,
        serviceDescription,
        MARGIN + 1,
        y,
        nameMaxWidth - 2,
        3.8
      );
      y += 1.5;
      doc.setTextColor(0);
      doc.setFontSize(10);
    }

    if (subs.length > 0) {
      for (const sub of subs) {
        const suffix = sub.isRequired ? " (incluso)" : "";
        const subLabel = `  • ${sub.name}${suffix}`;
        const subDescription = sub.description?.trim() ?? "";
        const subPrice = formatBudgetCurrency(sub.price);
        const subNameMaxWidth = showSubServicePrices ? 115 : CONTENT_WIDTH - 4;
        const subLines = doc.splitTextToSize(subLabel, subNameMaxWidth);
        const subDescriptionLines = subDescription
          ? doc.splitTextToSize(subDescription, subNameMaxWidth - 3)
          : [];

        if (
          y +
            subLines.length * 4 +
            subDescriptionLines.length * 3.6 >
          PAGE_HEIGHT - MARGIN - FOOTER_RESERVE_MM
        ) {
          doc.addPage();
          y = MARGIN;
        }

        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.setTextColor(110);
        doc.text(subLines, MARGIN + 2, y);
        if (showSubServicePrices) {
          doc.text(subPrice, PAGE_WIDTH - MARGIN, y, { align: "right" });
        }
        y += Math.max(subLines.length * 4, 5);

        if (subDescription) {
          doc.setFontSize(8);
          doc.setTextColor(140);
          y = drawWrappedDescription(
            doc,
            subDescription,
            MARGIN + 6,
            y,
            subNameMaxWidth - 3,
            3.6
          );
          y += 1;
        }
      }
      doc.setTextColor(0);
      doc.setFontSize(10);
    }

    y += 2;
  }

  y += 4;
  doc.line(MARGIN, y, PAGE_WIDTH - MARGIN, y);
  y += 8;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(0);
  doc.text(isPaidConfirmation ? "Total pago" : "Total", MARGIN, y);
  doc.text(formatBudgetCurrency(total), PAGE_WIDTH - MARGIN, y, {
    align: "right",
  });

  y = ensureFooterSpace(doc, y + 10);
  drawPdfFooter(doc, signature, isPaidConfirmation, options.footerText);

  const safeClient = clientName.replace(/\s+/g, "-").toLowerCase();
  const fileName =
    options.fileName ??
    `${options.filePrefix ??
      (isPaidConfirmation ? "comprovante-pagamento" : "orcamento")}-${budget.id}-${safeClient}`;
  doc.save(`${fileName}.pdf`);
}
